import { Fragment, useMemo, useRef } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { keyBy } from "lodash";

import { type DesignElement } from "@hooks/use-design-element-manager";

import { useHistoricalBudgetMix } from "../budget-mix-card/use-historical-budget-mix";

import { Bar } from "./bar";
import { useActiveHoveredChartRow } from "./hooks/use-active-hovered-chart-row";
import { useExpandedBreakdowns } from "./hooks/use-expanded-breakdowns";

export type IndexedBudgetMixChartDatum = {
  datumIndex: number;
} & BudgetMixChartDatum;

export enum TopBoxStrategy {
  SMALL_PERCENTAGE_BUCKET,
  PLATFORM_PLACEHOLDER,
  NONE,
}

type NoChartOffsetOptions = {
  shouldOffset: false;
};

type ChartWithOffsetOptions = {
  shouldOffset: true;
  offsetPercentage: number;
};

export type ChartOffsetOptions = NoChartOffsetOptions | ChartWithOffsetOptions;

type SmallPercentageTopBoxOptions = {
  strategy: TopBoxStrategy.SMALL_PERCENTAGE_BUCKET;
};

type NoPercentageTopBoxOptions = {
  strategy: TopBoxStrategy.NONE;
};

type PlatformPlaceholderTopBoxOptions = {
  strategy: TopBoxStrategy.PLATFORM_PLACEHOLDER;
  platformValue: string;
  platformPercentage: number;
};

export type TopBoxOptions =
  | SmallPercentageTopBoxOptions
  | PlatformPlaceholderTopBoxOptions
  | NoPercentageTopBoxOptions;

export type BudgetMixChartDatum = {
  breakdownKey: string;
  breakdownValue: string;
  percentage: number;
  spend: number;
  designElement: DesignElement;
  children: BudgetMixChartDatum[];
};

export type BudgetMixChartData = BudgetMixChartDatum[];

export const SegmentedBudgetMixChart = ({
  chartData,
  isHistorical,
  dateRange,
}: {
  chartData: BudgetMixChartData;
  isHistorical: boolean;
  dateRange: { startDate: string; endDate: string };
}) => {
  const { isBreakdownExpanded } = useExpandedBreakdowns();

  const [activeHoveredChartRow, setActiveHoveredChartRow] =
    useActiveHoveredChartRow();
  const maxNumber = chartData.reduce(
    (acc: number, curr: BudgetMixChartDatum) => acc + curr.percentage,
    0,
  );

  const { historicalBudgetMixData } = useHistoricalBudgetMix();
  const historicalBudgetMixDataMap = useMemo(
    () =>
      keyBy(
        historicalBudgetMixData,
        ({ breakdownKey, breakdownValue }) =>
          `${breakdownKey}:${breakdownValue}`,
      ),
    [historicalBudgetMixData],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop } = scrollRef.current;
      document
        .querySelectorAll(".budget-mix-scroll-container-ref")
        .forEach((el) => {
          el.scrollTop = scrollTop;
        });
    }
  };

  return (
    <div
      ref={scrollRef}
      className="my-2 text-xs font-semibold max-h-96 overflow-y-hidden hover:overflow-y-scroll w-full budget-mix-scroll-container-ref"
      style={{ scrollbarGutter: "stable" }}
      onScroll={handleScroll}
    >
      {chartData.map((data) => {
        const key = `${data.breakdownKey}:${data.breakdownValue}`;
        let canExpand = true;

        // If there is only one child and it is the same as the parent then there are no breakdowns so remove the child
        if (
          data.children.length === 1 &&
          data.children[0].breakdownKey === data.breakdownKey &&
          data.children[0].breakdownValue === data.breakdownValue
        ) {
          canExpand = false;
        }
        return (
          <Fragment key={key}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onMouseEnter={() => setActiveHoveredChartRow(key)}
              onMouseLeave={() => setActiveHoveredChartRow(null)}
              className={clsx("px-3", {
                "bg-v2-highlight-information": activeHoveredChartRow === key,
              })}
            >
              <Bar
                data={data}
                historicalBudgetMixData={historicalBudgetMixDataMap[key]}
                maxNumber={maxNumber}
                isHistorical={isHistorical}
                dateRange={dateRange}
                canExpand={canExpand}
              />
            </motion.div>
            <AnimatePresence>
              {isBreakdownExpanded(data.breakdownKey, data.breakdownValue) && (
                <motion.div
                  transition={{ ease: "easeOut", duration: 0.1 }}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "fit-content", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  {data.children.map((childData, childIndex) => {
                    const childKey = `${childData.breakdownKey}:${childData.breakdownValue}`;
                    return (
                      <motion.div
                        key={childKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onMouseEnter={() => setActiveHoveredChartRow(childKey)}
                        onMouseLeave={() => setActiveHoveredChartRow(null)}
                        className={clsx("px-3", {
                          "bg-cosmic-3": activeHoveredChartRow === childKey,
                        })}
                      >
                        <Bar
                          data={childData}
                          historicalBudgetMixData={
                            historicalBudgetMixDataMap[key]?.children[
                              childIndex
                            ]
                          }
                          maxNumber={maxNumber}
                          isHistorical={isHistorical}
                          dateRange={dateRange}
                          isChild
                        />
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </Fragment>
        );
      })}
    </div>
  );
};
